import { PromotionalType } from '../__autogenerated__/types/PromotionalType';

/** Footnote definition: arbitrary marker (†, ‡, ¹, etc.) + text. Rendered as "marker text" in footer. */
export type ShortFootnoteDef = {
  marker: string;
  text: string;
};

export type PromotionalTypeDisplay = {
  full: string;
  short: string;
  /**
   * Optional footnote for short form. When present, the footer will include this line (additive across types).
   * - Object: use marker + text (e.g. { marker: '†', text: '...' } → "† ...")
   * - String: use as the full footer line (legacy)
   */
  shortFootnote?: ShortFootnoteDef | string;
};

/**
 * Display text for promotional types.
 * - `full`: Used in UI tooltips and detailed views
 * - `short`: Used in Reddit markdown and compact views
 */
export const promotionalTypeDisplayMap: Record<string, PromotionalTypeDisplay> =
  {
    // Amazon
    [PromotionalType.PRIME_MEMBERSHIP]: {
      full: 'Requires Prime membership',
      short: 'w/ Prime',
    },

    // Bank of America Preferred Rewards
    [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_GOLD]: {
      full: 'With Preferred Rewards Gold',
      short: 'w/ PR Gold',
    },
    [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM]: {
      full: 'With Preferred Rewards Platinum',
      short: 'w/ PR Plat',
    },
    [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM_HONORS]: {
      full: 'With Preferred Rewards Platinum Honors',
      short: 'w/ PR Plat Honors',
    },
    [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_DIAMOND_HONORS]: {
      full: 'With Preferred Rewards Diamond Honors',
      short: 'w/ PR Diamond',
    },
    [PromotionalType.REQUIRE_BANK_OF_AMERICA_SMALL_BUSINESS_ACCOUNT]: {
      full: 'Requires BofA Small Business account',
      short: 'w/ BofA Biz',
    },

    // US Bank Smartly
    [PromotionalType.US_BANK_SMARTLY_TIER_1]: {
      full: 'With Smartly Tier 1',
      short: 'w/ Smartly $10k',
    },
    [PromotionalType.US_BANK_SMARTLY_TIER_2]: {
      full: 'With Smartly Tier 2',
      short: 'w/ Smartly $50k',
    },
    [PromotionalType.US_BANK_SMARTLY_TIER_3]: {
      full: 'With Smartly Tier 3',
      short: 'w/ Smartly $100k',
    },

    // Bilt
    [PromotionalType.BILT_CONNECT_TO_LYFT]: {
      full: 'Requires Bilt + Lyft connection',
      short: 'w/ Bilt+Lyft',
    },
    [PromotionalType.BILT_RENT_DAY]: {
      full: 'On Rent Day only',
      short: 'Rent Day',
    },
  [PromotionalType.BILT_SPEND_RATIO_25]: {
    full: 'With 25% spend ratio',
    short: 'Requires spend†',
    shortFootnote: {
      marker: '†',
      text: 'Rate based on spending ≥25% of housing payment on other purchases.',
    },
  },
  [PromotionalType.BILT_SPEND_RATIO_50]: {
    full: 'With 50% spend ratio',
    short: 'Requires spend†',
    shortFootnote: {
      marker: '†',
      text: 'Rate based on spending ≥50% of housing payment on other purchases.',
    },
  },
  [PromotionalType.BILT_SPEND_RATIO_75]: {
    full: 'With 75% spend ratio',
    short: 'Requires spend†',
    shortFootnote: {
      marker: '†',
      text: 'Rate based on spending ≥75% of housing payment on other purchases.',
    },
  },
  [PromotionalType.BILT_SPEND_RATIO_100]: {
    full: 'With 100% spend ratio',
    short: 'Requires spend†',
    shortFootnote: {
      marker: '†',
      text: 'Rate based on spending ≥100% of housing payment on other purchases.',
    },
  },

    // Upromise
    [PromotionalType.UPROMISE_COLLEGE_SAVINGS_PLAN]: {
      full: 'With Upromise account',
      short: 'w/ Upromise',
    },

    // Bloomingdale's
    [PromotionalType.BLOOMINGDALES_TOP_OF_LIST_TIER]: {
      full: 'With Top of List status',
      short: 'w/ Top of List',
    },
    [PromotionalType.BLOOMINGDALES_LOYALIST_TIER]: {
      full: 'With Loyalist status',
      short: 'w/ Loyalist',
    },
  };

/**
 * Get display text for a promotional type.
 * Falls back to formatting the type string if not found in the map.
 */
export function getPromotionalTypeDisplay(
  promotionalType: string,
  format: 'full' | 'short' = 'full'
): string {
  const display = promotionalTypeDisplayMap[promotionalType];
  if (display) {
    return display[format];
  }

  // Fallback: format the type string nicely
  const formatted = promotionalType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

  return format === 'short' ? `w/ ${formatted}` : `With ${formatted}`;
}

/**
 * Get the footer line for a promotional type when using the short form.
 * Returns a single line string (e.g. "† Rate based on...") or undefined.
 * Callers can collect these from multiple types and append all unique lines additively to the footer.
 */
export function getPromotionalTypeShortFootnote(
  promotionalType: string
): string | undefined {
  const def = promotionalTypeDisplayMap[promotionalType]?.shortFootnote;
  if (def == null) return undefined;
  if (typeof def === 'string') return def;
  return `${def.marker} ${def.text}`;
}
